import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons/faCircleMinus";
import { faTurnRight } from "@fortawesome/pro-solid-svg-icons/faTurnRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Flex, Text } from "@mantine/core";
import { type ReactNode, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { useSlimScreenerDetailsQuery } from "@/hooks/queries/screener-builder.queries";
import { useScreenerParamsWithNewCheck } from "@/hooks/screener-builder-hooks";
import { DerivedScreenerStatus, ScreenerStatus } from "@/types/screeners";
import { valueIncludedOrEqual } from "@/utils/helpers";

interface TableHeaderRightContentProps {
  menu: ReactNode;
  onPublishClick?: () => void;
}

const BADGE_CLASSNAMES = [
  "font-bold",
  "h-6",
  "px-3",
  "py-1",
  "text-xs",
  "uppercase",
];

export const TableHeaderRightContent = ({
  menu,
  onPublishClick,
}: TableHeaderRightContentProps) => {
  const [screenerId, idIsNew] = useScreenerParamsWithNewCheck();
  const { data: slimScreenerDetails } = useSlimScreenerDetailsQuery(
    screenerId,
    { enabled: !idIsNew },
  );
  const displayStatus = useMemo(
    () =>
      idIsNew
        ? DerivedScreenerStatus.Draft
        : (slimScreenerDetails?.derivedStatus ?? null),
    [idIsNew, slimScreenerDetails],
  );
  const badgeProps = useMemo(() => {
    switch (displayStatus) {
      case DerivedScreenerStatus.Draft:
        return {
          c: "gray.7",
          className: twMerge(BADGE_CLASSNAMES.concat("border-uw-gray-7")),
          color: "white",
          variant: "outline",
        };
      case DerivedScreenerStatus.Live:
        return {
          c: "#005648",
          color: "mint-green.1",
        };
      case DerivedScreenerStatus.Mixed:
        return {
          c: "#457069",
          color: "#EBFAF7",
        };
      case DerivedScreenerStatus.Paused:
        return {
          c: "gray.7",
          color: "gray.0",
        };
    }
  }, [displayStatus]);
  const showPublishUI = useMemo(
    () =>
      !!onPublishClick &&
      valueIncludedOrEqual(
        [ScreenerStatus.Preview, ScreenerStatus.Draft],
        slimScreenerDetails?.status,
      ),
    [onPublishClick, slimScreenerDetails],
  );

  return (
    <Flex align="center" gap={18}>
      {showPublishUI && (
        <>
          <Flex align="center">
            <FontAwesomeIcon className="text-uw-gray-5" icon={faCircleMinus} />
            <Text className="ml-2" size="sm">
              Unpublished changes
            </Text>
          </Flex>
          <Button
            onClick={onPublishClick}
            rightSection={<FontAwesomeIcon icon={faTurnRight} />}
            size="compact-sm"
          >
            Publish
          </Button>
        </>
      )}
      {badgeProps && (
        <Badge className={twMerge(BADGE_CLASSNAMES)} {...badgeProps}>
          {displayStatus}
        </Badge>
      )}
      {menu}
    </Flex>
  );
};
